import { Howl } from 'howler'
import * as Sentry from '@sentry/browser'

import { ReceptionNotificationSoundTones } from 'constants/Settings'

const DEFAULT_VOLUME = 0.5

const srcs: any = {
  Bell: require('../assets/sounds/Bell.mp3'),
  Harmonics: require('../assets/sounds/Harmonics.mp3'),
  Ring: require('../assets/sounds/Ring.mp3'),
}

let players: any = {}
let isInitialized = false

const initializePlayers = () => {
  if (isInitialized) return

  try {
    players = ReceptionNotificationSoundTones.reduce((ac, tone) => {
      try {
        const srcPath = typeof srcs[tone.id] === 'object' ? srcs[tone.id]?.default : srcs[tone.id]
        const player = new Howl({
          src: [srcPath],
          volume: DEFAULT_VOLUME,
          preload: true,
          html5: true,
        })
        return { ...ac, [tone.id]: player }
      } catch (error) {
        console.warn(`Failed to initialize sound player for ${tone.id}:`, error)
        Sentry.captureException(error, {
          tags: {
            component: 'SoundPlayer',
            operation: 'initialization'
          },
          extra: {
            toneId: tone.id,
            toneName: tone.name,
            audioSrc: srcs[tone.id]
          }
        })
        return ac
      }
    }, {})
    isInitialized = true
  } catch (error) {
    console.error('Failed to initialize sound players:', error)
    Sentry.captureException(error, {
      tags: {
        component: 'SoundPlayer',
        operation: 'bulk_initialization'
      },
      extra: {
        availableTones: ReceptionNotificationSoundTones.map(tone => tone.id),
        // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
        error: error.message
      }
    })
  }
}

export const playSound = (id: string, volume: number) => {
  if (!isInitialized) {
    initializePlayers()
  }

  const player = players[id]
  if (!player || typeof player.volume !== 'function' || typeof player.play !== 'function') {
    console.warn(`Sound player not found or invalid for id: ${id}`)
    Sentry.captureMessage(`Sound player not found or invalid`, {
      level: 'warning',
      extra: {
        requestedToneId: id,
        requestedVolume: volume,
        availablePlayers: Object.keys(players),
        playerExists: !!player,
        playerType: typeof player,
        hasVolumeMethod: player && typeof player.volume === 'function',
        hasPlayMethod: player && typeof player.play === 'function',
        isInitialized
      },
      tags: {
        component: 'SoundPlayer',
        operation: 'playback_validation'
      }
    })
    return
  }

  try {
    player.volume(volume)

    // 音声ファイルがロードされていない場合はロード完了を待つ
    if (player.state() === 'unloaded') {
      player.once('load', () => {
        player.play()
      })
      player.once('loaderror', (soundId: any, error: any) => {
        console.error('[SoundPlayer] Load error', { soundId, error })
      })
      player.load()
    } else {
      player.play()
    }
  } catch (error) {
    console.error(`Failed to play sound ${id}:`, error)

    // 詳細なエラー情報でSentryに送信
    Sentry.captureException(error, {
      tags: {
        component: 'SoundPlayer',
        operation: 'playback',
        // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
        isRetryable: error.name === 'InvalidStateError' || error.message?.includes('audio device')
      },
      extra: {
        toneId: id,
        requestedVolume: volume,
        // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
        errorName: error.name,
        // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
        errorMessage: error.message,
        userAgent: navigator.userAgent,
        audioContext: typeof window.AudioContext !== 'undefined',
        // TypeScriptの型定義にない場合があるためanyで回避
        webkitAudioContext: typeof (window as any).webkitAudioContext !== 'undefined'
      }
    })

    // WebKit系ブラウザでのリトライ処理
    // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
    if (error.name === 'InvalidStateError' || error.message?.includes('audio device')) {
      setTimeout(() => {
        try {
          player.play()
        } catch (retryError) {
          console.error(`Retry failed for sound ${id}:`, retryError)
          Sentry.captureException(retryError, {
            tags: {
              component: 'SoundPlayer',
              operation: 'playback_retry',
              // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
              originalError: error.name
            },
            extra: {
              toneId: id,
              // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
              originalErrorName: error.name,
              // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
              originalErrorMessage: error.message,
              // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
              retryErrorName: retryError.name,
              // @ts-ignore TypeScript4系移行時に発生した型エラーを回避
              retryErrorMessage: retryError.message
            }
          })
        }
      }, 100)
    }
  }
}
